import { AxiosError, AxiosResponse } from "axios";

const readHttpPayload = (
  result: unknown,
): { status: number; data: unknown } => {
  if (!result || typeof result !== "object") {
    return { status: 0, data: undefined };
  }

  const axiosError = result as AxiosError;
  if (axiosError.response) {
    return {
      status: axiosError.response.status ?? 0,
      data: axiosError.response.data,
    };
  }

  const axiosResponse = result as AxiosResponse;
  if (typeof axiosResponse.status === "number") {
    return {
      status: axiosResponse.status,
      data: axiosResponse.data,
    };
  }

  return { status: 0, data: undefined };
};

const GENERIC_HTTP_MESSAGES = new Set([
  "internal server error",
  "bad request",
  "not found",
  "unauthorized",
  "forbidden",
  "service unavailable",
  "gateway timeout",
]);

const asUsefulMessage = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || GENERIC_HTTP_MESSAGES.has(trimmed.toLowerCase())) {
    return null;
  }
  return trimmed;
};

const tryParseEmbeddedJson = (value: string): unknown | null => {
  const jsonStart = value.search(/[\[{]/);
  if (jsonStart === -1) {
    return null;
  }
  try {
    return JSON.parse(value.slice(jsonStart));
  } catch {
    return null;
  }
};

const messageFromPayload = (data: unknown, depth = 0): string | null => {
  if (depth > 6 || data == null) {
    return null;
  }

  if (typeof data === "string") {
    const parsed = tryParseEmbeddedJson(data);
    if (parsed != null) {
      return messageFromPayload(parsed, depth + 1);
    }
    return asUsefulMessage(data);
  }

  if (Array.isArray(data)) {
    for (const item of data) {
      const found = messageFromPayload(item, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  }

  if (typeof data === "object") {
    const record = data as Record<string, unknown>;
    const fromMessage = asUsefulMessage(record.message);
    if (fromMessage) {
      return fromMessage;
    }

    for (const key of ["error", "errors", "response", "detail"]) {
      if (key in record) {
        const found = messageFromPayload(record[key], depth + 1);
        if (found) {
          return found;
        }
      }
    }
  }

  return null;
};

/**
 * Extracts a user-facing message from an axios result or error.
 * Returns null when status is below 400.
 */
export const getApiErrorMessage = (
  result: unknown,
  fallbackMessage: string,
): string | null => {
  const { status, data } = readHttpPayload(result);

  if (status < 400) {
    return null;
  }

  return messageFromPayload(data) ?? fallbackMessage;
};

/** Same as getApiErrorMessage but always returns a string (for toast, etc.). */
export const getApiErrorMessageOrFallback = (
  result: unknown,
  fallbackMessage: string,
): string => getApiErrorMessage(result, fallbackMessage) ?? fallbackMessage;
